/**
 * User Role and Customer Access Helpers
 * 
 * These utilities handle the extraction of user roles and customer access
 * from Auth0 JWT tokens, supporting both legacy and new RBAC formats:
 * 
 * Legacy formats:
 * - Single customer: { customer: "store1", role: "admin" }
 * - Multi-customer: { customers: ["store1", "store2"], role: "admin" }
 * 
 * New RBAC format:
 * - Per-customer roles: { customer_roles: { "store1": "admin", "store2": "viewer" } }
 */

const AUDIENCE = 'https://reviews-api.konfidency.com.br';

export interface UserMetadata {
  customer?: string;
  customers?: string[];
  role?: string;
  customer_roles?: { [customer: string]: string };
}

export interface UserCustomerAccess {
  customers: string[];
  roles: { [customer: string]: string };
  primaryCustomer: string | null;
  isKonfidencyAdmin: boolean;
}

/**
 * Extract app_metadata from Auth0 user object
 */
export function getUserMetadata(user: any): UserMetadata | null {
  if (!user || !user[`${AUDIENCE}/app_metadata`]) {
    return null;
  }
  return user[`${AUDIENCE}/app_metadata`] as UserMetadata;
}

/**
 * Get comprehensive customer access information for a user
 */
export function getUserCustomerAccess(user: any): UserCustomerAccess {
  const metadata = getUserMetadata(user);
  
  if (!metadata) {
    return {
      customers: [],
      roles: {},
      primaryCustomer: null,
      isKonfidencyAdmin: false
    };
  }

  // Check if user is konfidency admin
  const isKonfidencyAdmin = metadata.customer === 'konfidency';
  
  if (isKonfidencyAdmin) {
    return {
      customers: ['konfidency'],
      roles: { konfidency: 'administrator' },
      primaryCustomer: 'konfidency',
      isKonfidencyAdmin: true
    };
  }

  const customers: string[] = [];
  const roles: { [customer: string]: string } = {};

  // Handle new per-customer roles format
  if (metadata.customer_roles) {
    for (const [customer, role] of Object.entries(metadata.customer_roles)) {
      if (!customers.includes(customer)) {
        customers.push(customer);
      }
      roles[customer] = role;
    }
  }
  
  // Handle legacy multi-customer format (always check, don't use else)
  if (metadata.customers && Array.isArray(metadata.customers)) {
    // Legacy users without explicit role should be administrators for backward compatibility
    const defaultRole = metadata.role || 'administrator';
    for (const customer of metadata.customers) {
      if (!customers.includes(customer)) {
        customers.push(customer);
      }
      // Only set role if not already set by customer_roles
      if (!roles[customer]) {
        roles[customer] = defaultRole;
      }
    }
  }
  
  // Handle legacy single customer format (always check, don't use else)
  if (metadata.customer && metadata.customer !== 'konfidency') {
    if (!customers.includes(metadata.customer)) {
      customers.push(metadata.customer);
    }
    // Only set role if not already set by customer_roles
    if (!roles[metadata.customer]) {
      // Legacy users without explicit role should be administrators for backward compatibility
      roles[metadata.customer] = metadata.role || 'administrator';
    }
  }

  return {
    customers,
    roles,
    primaryCustomer: metadata.customer || customers[0] || null,
    isKonfidencyAdmin: false
  };
}

/**
 * Check if user has access to a specific customer
 */
export function hasCustomerAccess(user: any, customer: string): boolean {
  const access = getUserCustomerAccess(user);
  
  // Konfidency admin has access to all customers
  if (access.isKonfidencyAdmin) {
    return true;
  }
  
  return access.customers.includes(customer);
}

/**
 * Get user's role for a specific customer
 */
export function getUserRoleForCustomer(user: any, customer: string): string | null {
  const access = getUserCustomerAccess(user);
  
  // Konfidency admin always has administrator role for all customers
  if (access.isKonfidencyAdmin) {
    return 'administrator';
  }
  
  return access.roles[customer] || null;
}

/**
 * Check if user has a specific role for a customer
 */
export function userHasRole(user: any, customer: string, role: string): boolean {
  const userRole = getUserRoleForCustomer(user, customer);
  return userRole === role;
}

/**
 * Check if user has admin access to a customer
 */
export function isCustomerAdmin(user: any, customer: string): boolean {
  return userHasRole(user, customer, 'administrator');
}

/**
 * Check if user has moderator or admin access to a customer
 */
export function canModerate(user: any, customer: string): boolean {
  const role = getUserRoleForCustomer(user, customer);
  return role === 'administrator' || role === 'moderator';
}

/**
 * Get the best default customer for user selection
 * Priority: localStorage > primaryCustomer > first available customer
 */
export function getDefaultCustomer(user: any): string | null {
  // Check localStorage first
  const storedCustomer = window.localStorage.getItem('activeCustomer');
  if (storedCustomer && hasCustomerAccess(user, storedCustomer)) {
    return storedCustomer;
  }

  const access = getUserCustomerAccess(user);
  
  // Use primary customer if available
  if (access.primaryCustomer && access.customers.includes(access.primaryCustomer)) {
    return access.primaryCustomer;
  }

  // Use first available customer
  return access.customers[0] || null;
}

/**
 * Set the active customer in localStorage
 */
export function setActiveCustomer(customer: string): void {
  window.localStorage.setItem('activeCustomer', customer);
}

/**
 * Get available customers formatted for dropdown components
 */
export function getCustomerOptions(user: any): Array<{ value: string; label: string; role?: string }> {
  const access = getUserCustomerAccess(user);
  
  return access.customers.map(customer => ({
    value: customer,
    label: customer,
    role: access.roles[customer]
  }));
}

/**
 * Check if user needs to see customer selector
 * Returns false if user only has access to one customer
 */
export function shouldShowCustomerSelector(user: any): boolean {
  const access = getUserCustomerAccess(user);
  return access.customers.length > 1;
}

// Role-based permission checking
export const PERMISSIONS = {
  // Customer management
  MANAGE_USERS: 'customer:users',
  MANAGE_SETTINGS: 'customer:settings',
  MANAGE_CUSTOMER: 'customer:write',
  
  // Review management  
  VIEW_REVIEWS: 'reviews:read',
  MODERATE_REVIEWS: 'reviews:moderate',
  EXPORT_REVIEWS: 'reviews:export',
  IMPORT_REVIEWS: 'reviews:import',
  CREATE_REVIEWS: 'reviews:write',
  
  // Questions management
  VIEW_QUESTIONS: 'questions:read',
  MODERATE_QUESTIONS: 'questions:moderate',
  EXPORT_QUESTIONS: 'questions:export',
  
  // Store reviews
  VIEW_STORE_REVIEWS: 'store-reviews:read',
  MODERATE_STORE_REVIEWS: 'store-reviews:moderate',
  EXPORT_STORE_REVIEWS: 'store-reviews:export',
  IMPORT_STORE_REVIEWS: 'store-reviews:import',
  
  // NPS management
  VIEW_NPS: 'nps:read',
  MANAGE_NPS: 'nps:write',
  
  // Integrations
  VIEW_INTEGRATIONS: 'integrations:read',
  MANAGE_INTEGRATIONS: 'integrations:write',
  
  // Platform migration
  PLATFORM_MIGRATE: 'platform:migrate',
  
  // Notifications
  VIEW_NOTIFICATIONS: 'notifications:read',
  MANAGE_NOTIFICATIONS: 'notifications:write',
  
  // Reports
  VIEW_AUDIT_REPORTS: 'reports:audit',
  VIEW_NOTIFICATIONS_REPORTS: 'reports:notifications',
  VIEW_ORDERS_REPORTS: 'reports:orders',
  
  // Billing
  VIEW_BILLING: 'billing:read',
  
  // WhatsApp
  VIEW_WHATSAPP: 'whatsapp:read',
  MANAGE_WHATSAPP: 'whatsapp:write',
  DISCONNECT_WHATSAPP: 'whatsapp:disconnect'
} as const;

/**
 * Role to permissions mapping (matches backend RBAC)
 */
const ROLE_PERMISSIONS = {
  administrator: [
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.MANAGE_SETTINGS,
    PERMISSIONS.MANAGE_CUSTOMER,
    PERMISSIONS.VIEW_REVIEWS,
    PERMISSIONS.MODERATE_REVIEWS,
    PERMISSIONS.EXPORT_REVIEWS,
    PERMISSIONS.IMPORT_REVIEWS,
    PERMISSIONS.CREATE_REVIEWS,
    PERMISSIONS.VIEW_QUESTIONS,
    PERMISSIONS.MODERATE_QUESTIONS,
    PERMISSIONS.EXPORT_QUESTIONS,
    PERMISSIONS.VIEW_STORE_REVIEWS,
    PERMISSIONS.MODERATE_STORE_REVIEWS,
    PERMISSIONS.EXPORT_STORE_REVIEWS,
    PERMISSIONS.IMPORT_STORE_REVIEWS,
    PERMISSIONS.VIEW_NPS,
    PERMISSIONS.MANAGE_NPS,
    PERMISSIONS.VIEW_INTEGRATIONS,
    PERMISSIONS.MANAGE_INTEGRATIONS,
    PERMISSIONS.VIEW_NOTIFICATIONS,
    PERMISSIONS.MANAGE_NOTIFICATIONS,
    PERMISSIONS.VIEW_AUDIT_REPORTS,
    PERMISSIONS.VIEW_NOTIFICATIONS_REPORTS,
    PERMISSIONS.VIEW_ORDERS_REPORTS,
    PERMISSIONS.VIEW_BILLING,
    PERMISSIONS.VIEW_WHATSAPP,
    PERMISSIONS.MANAGE_WHATSAPP,
    PERMISSIONS.DISCONNECT_WHATSAPP,
    PERMISSIONS.PLATFORM_MIGRATE
  ],
  moderator: [
    PERMISSIONS.VIEW_REVIEWS,
    PERMISSIONS.MODERATE_REVIEWS,
    PERMISSIONS.EXPORT_REVIEWS,
    PERMISSIONS.VIEW_QUESTIONS,
    PERMISSIONS.MODERATE_QUESTIONS,
    PERMISSIONS.EXPORT_QUESTIONS,
    PERMISSIONS.VIEW_STORE_REVIEWS,
    PERMISSIONS.MODERATE_STORE_REVIEWS,
    PERMISSIONS.EXPORT_STORE_REVIEWS,
    PERMISSIONS.VIEW_NPS,
    PERMISSIONS.VIEW_NOTIFICATIONS,
    PERMISSIONS.VIEW_NOTIFICATIONS_REPORTS,
    PERMISSIONS.VIEW_ORDERS_REPORTS,
    PERMISSIONS.VIEW_WHATSAPP
  ],
  viewer: [
    PERMISSIONS.VIEW_REVIEWS,
    PERMISSIONS.VIEW_QUESTIONS,
    PERMISSIONS.VIEW_STORE_REVIEWS,
    PERMISSIONS.VIEW_NPS,
    PERMISSIONS.VIEW_NOTIFICATIONS
  ]
} as const;

/**
 * Check if user has a specific permission for a customer
 */
export function hasPermission(user: any, customer: string, permission: string): boolean {
  const role = getUserRoleForCustomer(user, customer);
  if (!role) return false;
  
  const permissions = ROLE_PERMISSIONS[role as keyof typeof ROLE_PERMISSIONS] || [];
  return permissions.includes(permission as any);
}

/**
 * Get user's display name with role info
 */
export function getUserDisplayInfo(user: any, customer?: string): { name: string; email: string; role?: string } {
  const role = customer ? getUserRoleForCustomer(user, customer) : null;
  
  return {
    name: user?.name || user?.email || 'Unknown User',
    email: user?.email || '',
    role: role || undefined
  };
}